import React, { Fragment, useMemo, useState } from "react";

import moment from "moment";

import {
  ArrayUtils,
  formatDayDate,
  getAdminVerificationYearlyDataYear,
  getDocScanFieldResultKey,
  getEditingRequestButtonState,
  useGetFarmDocScanFields,
  useGetFieldPractices,
  useGetRedzoneExceptions,
  useInView,
  useUpdateReviewedDataPoints,
  useUpdateVerificationStatus,
} from "utils";

import { HARVEST_UNIT_VALUE, HarvestUnitValue, getUnitDisplayLabel, toTonnesPerHa } from "consts";

import { CROP_TYPE_FCPO, CropFCPO, EDITING_REQUEST_TYPE, FieldVerification, ReviewedDataPoint, VerificationEditingRequest, VerificationStatus } from "models";

import { Button, Col, ContextMenu, Flex, Icon, IconButton, Message, Row, SkeletonWrapper, Spin, TagNormal, Tooltip, Typography } from "components";

import { EvidenceRow } from "pages/admin/verifications/components/automations/EvidenceRow";
import { FieldEvidenceSelection } from "pages/admin/verifications/components/automations/FieldEvidenceDrawer";
import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { EditingRequestModal } from "pages/admin/verifications/components/misc/EditingRequestModal";
import { CROP_REVIEWED_DATA_POINT_OPTIONS, ReviewedDataPointsDropdown } from "pages/admin/verifications/components/misc/ReviewedDataPointsDropdown";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";
import { getDocScanEvidenceTitle, getDocScanRowSignals } from "pages/admin/verifications/utils/docScanEvidence";
import { getRedzoneExceptionDetails } from "pages/admin/verifications/utils/redzoneException";
import { getWarnings } from "pages/admin/verifications/utils/warnings";
import { CALENDAR_YEAR_START, FARM_YEAR_END, NEW_CROP } from "pages/app/all-field-view/constants/cropForm";

// Verifiers compare crops side by side, so everything is shown normalized to tonnes/ha rather than in
// whichever unit each farmer happened to enter.
const formatYield = (yieldValue: number | null, yieldUnit: HarvestUnitValue | null) =>
  `${toTonnesPerHa(yieldValue, yieldUnit) ?? 0} ${getUnitDisplayLabel(HARVEST_UNIT_VALUE.tonnesPerHa)}`;

type CropTableProps = {
  field: FieldVerification;
  farmId: string;
  editingRequests: VerificationEditingRequest[];
  onOpenEvidence: (selection: FieldEvidenceSelection) => void;
};

export const CropTable = ({ field, farmId, editingRequests, onOpenEvidence }: CropTableProps) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [editingRequestCrop, setEditingRequestCrop] = useState<CropFCPO | null>(null);
  const [missingCropRequestYear, setMissingCropRequestYear] = useState<number | null>(null);
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);

  const fieldId = field.id;

  const { data: fieldPractices, isLoading, isFetching } = useGetFieldPractices({ fieldId, inView, isAdmin: true });
  const { data: redzoneExceptions } = useGetRedzoneExceptions();
  const { data: docScanResults } = useGetFarmDocScanFields({ farmId });
  const isFieldInRedzone = !!field.redzone && field.redzone?.confirmed !== false;

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });
  const { mutate: updateReviewedDataPoints } = useUpdateReviewedDataPoints({ fieldId });

  // Handler for updating crop verification status
  const handleStatusChange = (cropId: string, newStatus: VerificationStatus) => {
    // Allow null to reset verification status
    updateStatus(
      {
        practiceType: "crops",
        practiceId: cropId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Crop verification status updated successfully" });
        },
      }
    );
  };

  // Handler for updating which data points were reviewed on a crop
  const handleReviewedDataPointsChange = (cropId: string, reviewedDataPoints: ReviewedDataPoint[] | null) => {
    updateReviewedDataPoints(
      { practiceType: "crops", practiceId: cropId, reviewedDataPoints },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Crop reviewed data points updated successfully" });
        },
      }
    );
  };

  // Helper function to get editing requests for a specific crop
  const getCropEditingRequests = (cropId: string): VerificationEditingRequest[] => editingRequests.filter((request) => request.fieldCropId === cropId);

  // Helper function to get missing crop editing requests for a specific year
  const getMissingCropEditingRequests = (year: number): VerificationEditingRequest[] =>
    editingRequests.filter((request) => request.requestType === EDITING_REQUEST_TYPE.CROP && request.fieldCropId === null && request.year === year);

  // Memoize the leaf props to prevent re-rendering issues
  const leafPropsMap = useMemo(() => {
    const map = new Map<string, { showTooltip: boolean; isOrange: boolean }>();
    fieldPractices?.crops?.forEach((crop) => {
      if (crop.hasResidue) {
        const shouldShowTooltip = Math.random() < 1 / 50; // EasterEgg: the golden leaf has a 1/50 chance of magically appearing
        map.set(crop.id, {
          showTooltip: shouldShowTooltip,
          isOrange: shouldShowTooltip,
        });
      }
    });
    return map;
  }, [fieldPractices?.crops]);

  // Easter egg: Marco's Magnificent Multiplier - 1/10 chance for crops with yield > 1 t/ha
  const marcoMultiplierMap = useMemo(() => {
    const map = new Map<string, boolean>();
    fieldPractices?.crops?.forEach((crop) => {
      if (crop.yieldPerHa && crop.yieldPerHa > 1) {
        const isMarcoMultiplier = Math.random() < 1 / 10;
        if (isMarcoMultiplier) {
          map.set(crop.id, true);
        }
      }
    });
    return map;
  }, [fieldPractices?.crops]);

  // Easter egg: Leon's Legendary Ledger - 1/10 chance for crops with all editing requests completed
  const leonLedgerMap = useMemo(() => {
    const map = new Map<string, boolean>();
    fieldPractices?.crops?.forEach((crop) => {
      const cropEditingRequests = editingRequests.filter((request) => request.fieldCropId === crop.id);
      const completedCount = cropEditingRequests.filter((req) => req.completedAt).length;
      if (cropEditingRequests.length > 0 && completedCount === cropEditingRequests.length) {
        const isLeonLedger = Math.random() < 1 / 10;
        if (isLeonLedger) {
          map.set(crop.id, true);
        }
      }
    });
    return map;
  }, [fieldPractices?.crops, editingRequests]);

  // Helper function to get leaf props
  const getLeafProps = (cropId: string) => leafPropsMap.get(cropId) || { showTooltip: false, isOrange: false };

  // Group crops by year
  const cropsByYear = ArrayUtils.groupBy(fieldPractices?.crops ?? [], (crop) => crop?.year ?? "unknown");

  // Inject verification year, if does not exist
  const verificationYear = getAdminVerificationYearlyDataYear();
  cropsByYear[verificationYear] = cropsByYear[verificationYear] ?? [];

  // Sort years in descending order
  const sortedYears = Object.keys(cropsByYear || {})
    .map(Number)
    .sort((a, b) => b - a);

  // Define crop type order: MAIN -> COVER -> UNDERSOWING
  const cropTypeOrder = [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

  // Function to sort crops by type
  const sortCropsByType = (crops: NonNullable<typeof fieldPractices>["crops"]) =>
    crops.sort((a, b) => {
      const aIndex = cropTypeOrder.indexOf(a.cropType);
      const bIndex = cropTypeOrder.indexOf(b.cropType);
      return aIndex - bIndex;
    });

  // Sort crops within each year by crop type
  const sortedCropsByYear = Object.fromEntries(sortedYears.map((year) => [year, sortCropsByType([...cropsByYear![year]])]));

  const warningsByCrop = getWarnings(fieldPractices?.crops ?? []);

  return (
    <div ref={ref} className="crop-table">
      {isFetching && !isLoading && <RefetchingOverlay />}
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={3} className="header-cell">
            crop name
          </Col>
          <Col md={1} className="header-cell">
            yield
          </Col>
          <Col md={2} className="header-cell">
            seeding start
          </Col>
          <Col md={2} className="header-cell">
            harvest end
          </Col>
          <Col md={2} className="header-cell">
            crop type
          </Col>
          <Col md={2} className="header-cell">
            selection type
          </Col>
          <Col md={3} className="header-cell">
            status
          </Col>
          <Col md={4} className="header-cell">
            reviewed data points
          </Col>
          <Col md={2} className="header-cell">
            editing requests
          </Col>
          <Col md={3} className="header-cell">
            actions
          </Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          const crops = sortedCropsByYear[year];
          const docScanResult = docScanResults?.get(getDocScanFieldResultKey(fieldId, year));
          const docScanCropId = docScanResult?.status ? crops.find(({ cropType }) => cropType === CROP_TYPE_FCPO.MAIN)?.id : undefined;
          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={3}>
                  <Flex align="center">
                    <Typography variant="h4" className="year-title">
                      {year}
                    </Typography>
                    <AddCropButton
                      onSelect={(cropType) =>
                        setCropToEdit(
                          NEW_CROP(cropType, moment(`${CALENDAR_YEAR_START}${year}`).toISOString(), moment(`${FARM_YEAR_END}${year}`).toISOString())
                        )
                      }
                    />
                  </Flex>
                </Col>
                <Col md={1}></Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
                <Col md={4}></Col>
                <Col md={2}>
                  <Typography variant="paragraph-xs">
                    {(() => {
                      const missingCropRequests: VerificationEditingRequest[] = getMissingCropEditingRequests(year);
                      return `${missingCropRequests.filter((req) => req.completedAt).length}/${missingCropRequests.length}`;
                    })()}
                  </Typography>
                </Col>
                <Col md={3}>
                  {(() => {
                    const missingCropRequests = getMissingCropEditingRequests(year);
                    const buttonState = getEditingRequestButtonState(missingCropRequests);

                    return (
                      <Button type={buttonState.type} className={buttonState.className} onClick={() => setMissingCropRequestYear(year)}>
                        {buttonState.text}
                      </Button>
                    );
                  })()}
                </Col>
              </Row>
              {/* Crops for this year */}
              {crops.map((crop) => (
                <Fragment key={crop.id}>
                  <Row className="crop-row pointer" onClick={() => setCropToEdit(crop)}>
                    <Col md={3} className="crop-cell">
                      <div className="crop-name-cell">
                        <div className={`crop-indicator ${crop.verificationStatus}`} />
                        <Typography variant="paragraph-s" bold>
                          {crop.cropName}
                        </Typography>
                        {crop.hasResidue && (
                          <Tooltip
                            title={
                              getLeafProps(crop.id).showTooltip
                                ? "You found the Mattis Golden Leaf™ — rare item 🍃✨ Grants +10 residue insight and +5 verification power. (and yes this crop left residue)"
                                : ""
                            }
                          >
                            <Icon
                              iconName="tabler/leaf"
                              className={`icon inline margin-left-50 ${getLeafProps(crop.id).isOrange ? "orange-text" : "green-text"}`}
                              size="s"
                            />
                          </Tooltip>
                        )}

                        {warningsByCrop[crop.id]?.length > 0 && (
                          <Tooltip
                            title={warningsByCrop[crop.id]?.map(({ title, description }, i) => (
                              <Fragment key={crop.id}>
                                <Typography variant="paragraph-s" color="white" key={i}>
                                  {title}: {description}
                                </Typography>
                                <br />
                                <br />
                              </Fragment>
                            ))}
                          >
                            <TagNormal color="red" text={warningsByCrop[crop.id].length} />
                          </Tooltip>
                        )}

                        {(() => {
                          const redzoneExceptionDetails = getRedzoneExceptionDetails(crop, isFieldInRedzone, redzoneExceptions);
                          if (!redzoneExceptionDetails) return null;
                          return (
                            <Tooltip
                              title={
                                <>
                                  <Typography variant="paragraph-s" color="white">
                                    Reason: {redzoneExceptionDetails.reason}
                                  </Typography>
                                  {redzoneExceptionDetails.explanation && (
                                    <Typography variant="paragraph-s" color="white">
                                      Explanation: {redzoneExceptionDetails.explanation}
                                    </Typography>
                                  )}
                                </>
                              }
                            >
                              <TagNormal color="orange" text="red zone" className="redzone-exception-tag" />
                            </Tooltip>
                          );
                        })()}
                      </div>
                    </Col>
                    <Col md={1} className={`crop-cell ${crop.yieldSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      {(() => {
                        const isMarcoMultiplier = marcoMultiplierMap.has(crop.id);
                        return (
                          <Tooltip
                            title={
                              isMarcoMultiplier
                                ? "🎰 Marco's Magnificent Multiplier™ activated! A bountiful harvest awaits. +20 Harvest Mastery, +15 Yield Excellence"
                                : ""
                            }
                          >
                            <Typography variant="paragraph-xs">
                              {isMarcoMultiplier && "🎰 "}
                              {formatYield(crop.yieldPerHa, crop.yieldPerHaUnit ?? null)}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </Col>
                    <Col md={2} className={`crop-cell ${crop.growingDatesSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <Typography variant="paragraph-xs">{crop.seedingStartDate ? formatDayDate(crop.seedingStartDate) : "-"}</Typography>
                    </Col>
                    <Col md={2} className={`crop-cell ${crop.growingDatesSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <Typography variant="paragraph-xs">{crop.harvestEndDate ? formatDayDate(crop.harvestEndDate) : "-"}</Typography>
                    </Col>
                    <Col md={2} className="crop-cell">
                      <TagNormal color="grey" text={crop.cropType?.toLowerCase()} />
                    </Col>
                    <Col md={2} className="crop-cell">
                      {crop.selectionType ? <TagNormal color="blue" text={crop.selectionType} /> : "-"}
                    </Col>
                    <Col md={3} className="crop-cell">
                      <VerificationStatusDropdown status={crop.verificationStatus ?? null} cropId={crop.id} onStatusChange={handleStatusChange} />
                    </Col>
                    <Col md={4} className="crop-cell">
                      <ReviewedDataPointsDropdown
                        reviewedDataPoints={crop.reviewedDataPoints ?? null}
                        practiceId={crop.id}
                        options={CROP_REVIEWED_DATA_POINT_OPTIONS}
                        onChange={handleReviewedDataPointsChange}
                      />
                    </Col>
                    <Col md={2} className="crop-cell">
                      {(() => {
                        const cropEditingRequests = getCropEditingRequests(crop.id);
                        const completedCount = cropEditingRequests.filter((req) => req.completedAt).length;
                        const totalCount = cropEditingRequests.length;
                        const isLeonLedger = leonLedgerMap.has(crop.id);
                        return (
                          <Tooltip
                            title={
                              isLeonLedger
                                ? "📚 Leon's Legendary Ledger™ unfolds! All records in perfect order. +18 Documentation Excellence, +7 Admin Aura"
                                : ""
                            }
                          >
                            <Typography variant="paragraph-xs" className={isLeonLedger ? "orange-text" : ""}>
                              {isLeonLedger && "📚 "}
                              {`${completedCount}/${totalCount}`}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </Col>
                    <Col md={3} className="crop-cell">
                      {(() => {
                        const cropEditingRequests = getCropEditingRequests(crop.id);
                        const buttonState = getEditingRequestButtonState(cropEditingRequests);

                        return (
                          <Button
                            type={buttonState.type}
                            className={buttonState.className}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRequestCrop(crop);
                            }}
                          >
                            {buttonState.text}
                          </Button>
                        );
                      })()}
                    </Col>
                  </Row>
                  {docScanResult && crop.id === docScanCropId && (
                    <div className="crop-row-evidence">
                      <EvidenceRow
                        signals={docScanResult.isCorrect === false ? [] : getDocScanRowSignals(docScanResult)}
                        title={getDocScanEvidenceTitle(docScanResult)}
                        onClick={() =>
                          onOpenEvidence({
                            fieldId: field.id,
                            fieldName: field.name,
                            year,
                            defaultTabKey: "docScan",
                            docScanFieldId: docScanResult.scanFieldId,
                          })
                        }
                      />
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <Typography variant="paragraph-s" className="text-disabled">
                No crops found for this field
              </Typography>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>
      <EditingRequestModal
        target={editingRequestCrop ? { requestType: EDITING_REQUEST_TYPE.CROP, id: editingRequestCrop.id, label: editingRequestCrop.cropName } : undefined}
        missingCropYear={missingCropRequestYear}
        fieldId={fieldId}
        showModal={!!(editingRequestCrop || missingCropRequestYear)}
        onClose={() => {
          setEditingRequestCrop(null);
          setMissingCropRequestYear(null);
        }}
        farmId={farmId}
        editingRequests={
          missingCropRequestYear
            ? editingRequests.filter(
                (req) => req.fieldId === fieldId && req.requestType === EDITING_REQUEST_TYPE.CROP && req.year === missingCropRequestYear && !req.fieldCropId
              )
            : editingRequests
        }
        verificationYear={verificationYear}
      />
      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
    </div>
  );
};

const AddCropButton = ({ onSelect }: { onSelect: (cropType: CROP_TYPE_FCPO) => void }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <>
      <IconButton
        iconName="klim/plus"
        type="tertiary"
        size="small"
        className="margin-left-50"
        tooltip={{ title: "Add crop" }}
        onClick={() => setShowMenu(true)}
      />

      <ContextMenu
        items={[
          {
            iconName: "klim/crops-matured",
            label: "Main crop",
            onClick: () => onSelect(CROP_TYPE_FCPO.MAIN),
          },
          {
            iconName: "klim/crops-branching",
            label: "Cover crop",
            onClick: () => onSelect(CROP_TYPE_FCPO.COVER),
          },
          {
            iconName: "klim/crops-emerging",
            label: "Undersowing",
            onClick: () => onSelect(CROP_TYPE_FCPO.UNDERSOWING),
          },
        ]}
        placement="bottom-left"
        visible={showMenu}
        onClose={() => setShowMenu(false)}
      />
    </>
  );
};

const RefetchingOverlay = () => (
  <div className="crop-table-refetching-overlay">
    <Flex vertical={true} align="center" gap="1rem">
      <Spin size="large" />
    </Flex>
  </div>
);
